import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper, Avatar
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  ArrowBack as ArrowBackIcon, AttachMoney as MoneyIcon, Event as EventIcon,
  Business as BusinessIcon, Person as PersonIcon,
  SwapHoriz as TransferIcon, CheckCircle as WonIcon,
  Warning as RiskIcon, LocationOn as LocationIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmOpportunity, OpportunityStage, ProjectType, ProjectSegment, ProductGamme, TransferStatus, CrmContact, CrmOrganisation } from '../types/crm';

const emptyOpportunity = (): Partial<CrmOpportunity> => ({
  title: '',
  refInterne: '',
  typeProjet: 'PROMOTION',
  segment: 'PRIVE',
  wilaya: 'Alger',
  chantierAddress: '',
  gamme: 'URBANIA',
  units: 100,
  unitPrice: 45000,
  discount: 10,
  extraDiscount: 0,
  progress: 25,
  priority: 'MEDIUM',
  risk: 'LOW',
  stage: 'PROSPECTING',
  amount: 4050000,
  probability: 20,
  closeDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  transferStatus: 'NONE',
  actionRequired: '',
  notes: '',
});

const getStageColor = (stage: OpportunityStage): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (stage) {
    case 'PROSPECTING': return 'info';
    case 'QUALIFICATION': return 'primary';
    case 'PROPOSAL': return 'warning';
    case 'NEGOTIATION': return 'secondary';
    case 'WON': return 'success';
    case 'LOST': return 'error';
    case 'ON_HOLD': return 'default';
    default: return 'default';
  }
};

const getStageLabel = (stage: OpportunityStage, t: (key: string, fallback?: string) => string): string => {
  switch (stage) {
    case 'PROSPECTING': return t('crm.opportunities.stageProspecting', 'Prospecté');
    case 'QUALIFICATION': return t('crm.opportunities.stageQualification', 'En consultation');
    case 'PROPOSAL': return t('crm.opportunities.stageProposal', 'Proposition');
    case 'NEGOTIATION': return t('crm.opportunities.stageNegotiation', 'En négociation');
    case 'WON': return t('crm.opportunities.stageWon', 'Gagné (Prêt transfert)');
    case 'LOST': return t('crm.opportunities.stageLost', 'Perdu');
    case 'ON_HOLD': return t('crm.opportunities.stageOnHold', 'À l\'arrêt');
    default: return stage;
  }
};

const getGammeColor = (gamme?: ProductGamme): 'primary' | 'secondary' | 'warning' | 'info' => {
  switch (gamme) {
    case 'URBANIA': return 'primary';
    case 'FUTURA': return 'secondary';
    case 'DOORIA': return 'warning';
    default: return 'info';
  }
};

export const OpportunitiesPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [opportunitiesList, setOpportunitiesList] = useState<CrmOpportunity[]>([]);
  const [contactsList, setContactsList] = useState<CrmContact[]>([]);
  const [organisationsList, setOrganisationsList] = useState<CrmOrganisation[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmOpportunity>>(emptyOpportunity());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [opps, contacts, orgs] = await Promise.all([
        crmApi.getOpportunities(),
        crmApi.getContacts(),
        crmApi.getOrganisations()
      ]);
      setOpportunitiesList(opps);
      setContactsList(contacts);
      setOrganisationsList(orgs);
    } catch (err) {
      console.error('Error loading CRM opportunities:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyOpportunity());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (opp: CrmOpportunity) => {
    setFormData({ ...opp });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const calculateAmount = (units: number, unitPrice: number, discount: number, extraDiscount: number) => {
    const totalGross = units * unitPrice;
    const discountRate = (discount + extraDiscount) / 100;
    return Math.max(0, Math.round(totalGross * (1 - discountRate)));
  };

  const handlePricingChange = (fields: { units?: number; unitPrice?: number; discount?: number; extraDiscount?: number }) => {
    const units = fields.units ?? formData.units ?? 0;
    const unitPrice = fields.unitPrice ?? formData.unitPrice ?? 0;
    const discount = fields.discount ?? formData.discount ?? 0;
    const extraDiscount = fields.extraDiscount ?? formData.extraDiscount ?? 0;
    const computedAmount = calculateAmount(units, unitPrice, discount, extraDiscount);

    setFormData({
      ...formData,
      ...fields,
      amount: computedAmount
    });
  };

  const handleTransferToKam = async (opp: CrmOpportunity) => {
    const oppId = (opp.id || opp.COP_IDF)!;
    const confirmed = await blobticMessage.confirm({
      title: 'Passation Opérationnelle vers le KAM',
      message: `Confirmez-vous le transfert officiel du projet "${opp.title}" à l'équipe KAM ?\nUn nouveau chantier opérationnel sera automatiquement initialisé.`
    });

    if (confirmed) {
      setLoading(true);
      try {
        const res = await crmApi.transferOpportunityToKam(oppId, 'Karim Benali');
        if (res.success) {
          blobticMessage.success('Projet transféré au KAM avec succès ! Chantier opérationnel créé.');
          loadData();
        } else {
          blobticMessage.error(res.message || 'Erreur lors du transfert.');
        }
      } catch (e: any) {
        blobticMessage.error(e.message || 'Erreur lors du transfert.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (opp: CrmOpportunity) => {
    const oppId = (opp.id || opp.COP_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.opportunities.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette opportunité ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteOpportunity(oppId);
      if (res.success) {
        blobticMessage.success(t('crm.opportunities.deleteSuccess', 'Opportunité supprimée avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('crm.opportunities.titleRequired', 'Le titre du projet / opportunité est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      COP_IDF: formData.id || formData.COP_IDF,
      COP_CODE: formData.code || formData.COP_CODE,
      COP_TITLE: formData.title,
      CCT_IDF: formData.contactId || formData.CCT_IDF,
      COR_IDF: formData.organisationId || formData.COR_IDF,
      COP_REF_INTERNE: formData.refInterne,
      COP_TYPE_PROJET: formData.typeProjet,
      COP_SEGMENT: formData.segment,
      COP_CHANTIER_ADDRESS: formData.chantierAddress,
      COP_WILAYA: formData.wilaya,
      COP_GAMME: formData.gamme,
      COP_UNITS: formData.units,
      COP_UNIT_PRICE: formData.unitPrice,
      COP_DISCOUNT: formData.discount,
      COP_EXTRA_DISCOUNT: formData.extraDiscount,
      COP_PROGRESS: formData.progress,
      COP_PRIORITY: formData.priority,
      COP_RISK: formData.risk,
      COP_STAGE: formData.stage,
      COP_AMOUNT: Number(formData.amount || 0),
      COP_PROBABILITY: Number(formData.probability || 0),
      COP_CLOSEDATE: formData.closeDate,
      COP_TRANSFER_STATUS: formData.transferStatus,
      COP_ACTION_REQUIRED: formData.actionRequired,
      COP_NOTES: formData.notes,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.opportunities.editTitle', 'Fiche Projet / Opportunité') : t('crm.opportunities.newTitle', 'Nouveau Projet / Opportunité')}
        icon={<TrendingUpIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Stack direction="row" spacing={1}>
            {isEditing && formData.transferStatus !== 'TRANSFERRED' && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<TransferIcon />}
                onClick={() => handleTransferToKam(formData as CrmOpportunity)}
              >
                {t('crm.opportunities.transferBtn', 'Transférer au KAM')}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={backToList}
            >
              {t('common.backToList', 'Retour à la liste')}
            </Button>
          </Stack>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_opportunity', primaryKey: 'COP_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Identification Projet & Acteurs */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" /> {t('crm.opportunities.secIdent', 'Identification du Projet & Acteurs')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('common.code', 'Code Projet')}
                      value={formData.code || t('common.autoGenerated', '(Automatique)')}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.opportunities.titleField', 'Nom / Intitulé du Projet')}
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.refInterne', 'Référence Interne Dossier')}
                      value={formData.refInterne || ''}
                      onChange={(e) => setFormData({ ...formData, refInterne: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.opportunities.typeProjet', 'Type de Projet')}
                      value={formData.typeProjet || 'PROMOTION'}
                      onChange={(e) => setFormData({ ...formData, typeProjet: e.target.value as ProjectType })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PROMOTION">{t('crm.opportunities.typePromotion', 'Promotion Immobilière')}</MenuItem>
                      <MenuItem value="AADL">{t('crm.opportunities.typeAadl', 'Programme AADL')}</MenuItem>
                      <MenuItem value="LPP">{t('crm.opportunities.typeLpp', 'Programme LPP')}</MenuItem>
                      <MenuItem value="PUBLIC">{t('crm.opportunities.typePublic', 'Marché Public Équipement')}</MenuItem>
                      <MenuItem value="PRIVE">{t('crm.opportunities.typePrive', 'Particulier / Privé Haut Standing')}</MenuItem>
                      <MenuItem value="INDUSTRIEL">{t('crm.opportunities.typeIndustriel', 'Projet Industriel / Tertiaire')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.opportunities.segment', 'Segment')}
                      value={formData.segment || 'PRIVE'}
                      onChange={(e) => setFormData({ ...formData, segment: e.target.value as ProjectSegment })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PRIVE">{t('crm.opportunities.segmentPrive', 'Secteur Privé')}</MenuItem>
                      <MenuItem value="PUBLIC">{t('crm.opportunities.segmentPublic', 'Secteur Public')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.opportunities.organisation', 'Organisation (BET / Promo)')}
                      value={formData.organisationId || ''}
                      onChange={(e) => setFormData({ ...formData, organisationId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('common.none', '-- Aucune --')}</MenuItem>
                      {organisationsList.map((org) => (
                        <MenuItem key={org.id || org.COR_IDF} value={org.id || org.COR_IDF}>
                          {org.name} ({org.type})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.opportunities.contact', 'Contact Décideur')}
                      value={formData.contactId || ''}
                      onChange={(e) => setFormData({ ...formData, contactId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('common.none', '-- Aucun --')}</MenuItem>
                      {contactsList.map((c) => (
                        <MenuItem key={c.id || c.CCT_IDF} value={c.id || c.CCT_IDF}>
                          {c.firstName} {c.lastName} ({c.company || 'Contact'})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.opportunities.wilaya', 'Wilaya / Région')}
                      value={formData.wilaya || ''}
                      onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label={t('crm.opportunities.chantierAddress', 'Adresse Précise du Chantier')}
                      value={formData.chantierAddress || ''}
                      onChange={(e) => setFormData({ ...formData, chantierAddress: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Détails Techniques & Chiffrage */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" /> {t('crm.opportunities.secSpecs', 'Détails Techniques & Chiffrage Devis')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.opportunities.gamme', 'Gamme de Produits')}
                      value={formData.gamme || 'URBANIA'}
                      onChange={(e) => setFormData({ ...formData, gamme: e.target.value as ProductGamme })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="URBANIA">{t('crm.gamme.urbaniaShort', 'Urbania (PVC Fenêtres & Coulissants)')}</MenuItem>
                      <MenuItem value="FUTURA">{t('crm.gamme.futuraShort', 'Futura (Haute Isolation Thermique)')}</MenuItem>
                      <MenuItem value="DOORIA">{t('crm.gamme.dooriaShort', 'Dooria (Portes Blindées / Entrée)')}</MenuItem>
                      <MenuItem value="MIXTE">{t('crm.gamme.mixteShort', 'Solutions Mixtes')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.units', 'Unités de Menuiserie')}
                      type="number"
                      value={formData.units ?? 0}
                      onChange={(e) => handlePricingChange({ units: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.unitPrice', 'Prix HT Unitaire (DZD)')}
                      type="number"
                      value={formData.unitPrice ?? 0}
                      onChange={(e) => handlePricingChange({ unitPrice: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.discount', 'Remise Accordée (%)')}
                      type="number"
                      value={formData.discount ?? 0}
                      onChange={(e) => handlePricingChange({ discount: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.extraDiscount', 'Remise Supplémentaire (%)')}
                      type="number"
                      value={formData.extraDiscount ?? 0}
                      onChange={(e) => handlePricingChange({ extraDiscount: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.amount', 'Montant Net Estimé (DZD)')}
                      type="number"
                      value={formData.amount ?? 0}
                      onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('common.stage', 'Étape Pipeline')}
                      value={formData.stage || 'PROSPECTING'}
                      onChange={(e) => setFormData({ ...formData, stage: e.target.value as OpportunityStage })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PROSPECTING">{t('crm.stage.prospecting', 'Prospecté')}</MenuItem>
                      <MenuItem value="QUALIFICATION">{t('crm.stage.qualification', 'En Consultation')}</MenuItem>
                      <MenuItem value="PROPOSAL">{t('crm.stage.proposal', 'Proposition / Devis')}</MenuItem>
                      <MenuItem value="NEGOTIATION">{t('crm.stage.negotiation', 'En Négociation')}</MenuItem>
                      <MenuItem value="WON">{t('crm.stage.won', 'Gagné (Transfert KAM)')}</MenuItem>
                      <MenuItem value="LOST">{t('crm.stage.lost', 'Perdu')}</MenuItem>
                      <MenuItem value="ON_HOLD">{t('crm.stage.onHold', 'À l\'Arrêt')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.opportunities.closeDate', 'Échéance Concrétisation')}
                      type="date"
                      value={formData.closeDate || ''}
                      onChange={(e) => setFormData({ ...formData, closeDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 3: Suivi Commercial & Passation KAM */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TransferIcon color="primary" /> {t('crm.opportunities.secTransfer', 'Suivi Commercial & Transmission KAM')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('crm.opportunities.commercialProgress', 'Avancement Commercial')} : {formData.progress || 0}%
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={formData.progress || 0}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <TextField
                          type="number"
                          value={formData.progress ?? 0}
                          onChange={(e) => setFormData({ ...formData, progress: Number(e.target.value) })}
                          sx={{ width: 80 }}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.opportunities.priority', 'Priorité')}
                      value={formData.priority || 'MEDIUM'}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="LOW">{t('crm.priority.low', 'Basse')}</MenuItem>
                      <MenuItem value="MEDIUM">{t('crm.priority.medium', 'Moyenne')}</MenuItem>
                      <MenuItem value="HIGH">{t('crm.priority.high', 'Haute')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.opportunities.risk', 'Risque Commercial')}
                      value={formData.risk || 'LOW'}
                      onChange={(e) => setFormData({ ...formData, risk: e.target.value as any })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="LOW">{t('crm.risk.low', 'Faible')}</MenuItem>
                      <MenuItem value="MEDIUM">{t('crm.risk.medium', 'Moyen')}</MenuItem>
                      <MenuItem value="HIGH">{t('crm.risk.high', 'Élevé')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.opportunities.actionRequired', 'Prochaine Action Demandée')}
                      value={formData.actionRequired || ''}
                      onChange={(e) => setFormData({ ...formData, actionRequired: e.target.value })}
                      placeholder={t('crm.opportunities.actionPlaceholder', 'Ex: Envoi de contre-offre avec remise supplémentaire, relance après validation CDC...')}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('common.notes', 'Notes Internes & Historique')}
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                      placeholder={t('crm.opportunities.notesPlaceholder', 'Observations sur le maître d\'ouvrage, les concurrents identifiés...')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.opportunities.title', 'Projets & Opportunités Commerciales')}
      icon={<TrendingUpIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {opportunitiesList.map((opp) => {
            const oppId = opp.id || opp.COP_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={oppId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={opp.title}>
                          {opp.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opp.code} • {opp.wilaya || 'Algérie'}
                        </Typography>
                      </Box>
                      <Chip
                        label={getStageLabel(opp.stage, t)}
                        color={getStageColor(opp.stage)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {opp.gamme && (
                          <Chip
                            label={`${t('crm.opportunities.gamme', 'Gamme')} ${t(opp.gamme, opp.gamme)}`}
                            color={getGammeColor(opp.gamme)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {opp.units ? (
                          <Chip label={`${opp.units} ${t('crm.opportunities.units', 'unités')}`} size="small" variant="outlined" />
                        ) : null}
                        <Chip label={t(opp.typeProjet || 'PROMOTION', opp.typeProjet || 'PROMOTION')} size="small" variant="outlined" />
                      </Stack>

                      {opp.organisationName && (
                        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                          🏢 {opp.organisationName}
                        </Typography>
                      )}

                      {opp.contactName && (
                        <Typography variant="body2" color="text.secondary">
                          👤 {opp.contactName}
                        </Typography>
                      )}

                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        {(opp.amount || 0).toLocaleString()} DZD
                      </Typography>

                      {/* Avancement & Statut transfert */}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('crm.opportunities.progress', 'Avancement')}</Typography>
                          <Typography variant="caption" fontWeight={700}>{opp.progress || 0}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={opp.progress || 0}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      {opp.transferStatus === 'TRANSFERRED' ? (
                        <Chip
                          icon={<WonIcon fontSize="small" />}
                          label={t('crm.opportunities.transferredKam', 'Transféré au KAM (Chantier Actif)')}
                          size="small"
                          color="success"
                        />
                      ) : opp.transferStatus === 'PENDING_TRANSFER' ? (
                        <Chip
                          icon={<TransferIcon fontSize="small" />}
                          label={t('crm.opportunities.readyTransferKam', 'Prêt pour passation KAM')}
                          size="small"
                          color="warning"
                        />
                      ) : null}

                      {opp.actionRequired && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            bgcolor: 'action.hover',
                            p: 1,
                            borderRadius: 1
                          }}
                        >
                          📌 {opp.actionRequired}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {opp.closeDate ? `${t('crm.opportunities.closeTarget', 'Échéance')}: ${new Date(opp.closeDate).toLocaleDateString()}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {opp.transferStatus !== 'TRANSFERRED' && (
                        <Tooltip title={t('crm.opportunities.transferBtn', 'Transférer au KAM')}>
                          <IconButton size="small" color="secondary" onClick={() => handleTransferToKam(opp)}>
                            <TransferIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(opp)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(opp)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 120 },
            { id: 'title', label: t('crm.opportunities.titleField', 'Projet'), minWidth: 200 },
            {
              id: 'gamme',
              label: t('crm.opportunities.gamme', 'Gamme'),
              width: 120,
              chip: true,
              chipColorMap: {
                URBANIA: 'primary',
                FUTURA: 'secondary',
                DOORIA: 'warning',
                MIXTE: 'info'
              }
            },
            { id: 'units', label: t('crm.opportunities.units', 'Unités'), width: 90 },
            { id: 'organisationName', label: t('crm.opportunities.organisation', 'Organisation'), width: 170 },
            { id: 'contactName', label: t('crm.opportunities.contact', 'Contact'), width: 150 },
            {
              id: 'stage',
              label: t('common.stage', 'Étape'),
              width: 140,
              chip: true,
              chipColorMap: {
                PROSPECTING: 'info',
                QUALIFICATION: 'primary',
                PROPOSAL: 'warning',
                NEGOTIATION: 'secondary',
                WON: 'success',
                LOST: 'error',
                ON_HOLD: 'default'
              }
            },
            { id: 'amount', label: t('crm.opportunities.amount', 'Montant (DZD)'), width: 150, format: (val: any) => `${Number(val || 0).toLocaleString()} DZD` },
            { id: 'progress', label: t('crm.opportunities.progress', 'Avancement'), width: 110, format: (val: any) => `${val}%` },
            { id: 'transferStatus', label: t('crm.opportunities.transferStatus', 'Passation'), width: 130, chip: true },
          ]}
          data={opportunitiesList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.opportunities.search', 'Rechercher un projet, gamme, prescripteur...')}
          filterColumns={['code', 'title', 'gamme', 'organisationName', 'contactName', 'stage', 'transferStatus']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default OpportunitiesPage;
